import React, { useState, useEffect, useRef } from 'react';

function ApiDocsPage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [bearerToken, setBearerToken] = useState('');
  const [error, setError] = useState('');
  const [loginError, setLoginError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [swaggerLoaded, setSwaggerLoaded] = useState(false);
  const swaggerUIRef = useRef(null);

  useEffect(() => {
    if (window.SwaggerUIBundle) {
      setSwaggerLoaded(true);
      return;
    }

    // Load CSS
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = '/swagger-ui/swagger-ui.css';
    document.head.appendChild(link);

    // Load JS
    const script = document.createElement('script');
    script.src = '/swagger-ui/swagger-ui.js';
    script.onload = () => setSwaggerLoaded(true);
    script.onerror = () => setError('Failed to load Swagger UI');
    document.head.appendChild(script);

    return () => {
      if (link.parentNode) link.parentNode.removeChild(link);
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, []);

  // ตรวจ cookie auth เดิม
  useEffect(() => {
    fetch('/cms-swagger-spec')
      .then((r) => {
        if (r.status === 404) {
          // SWAGGER_PASSWORD not configured - show not found immediately
          setLoading(false);
          setError('notConfigured');
          return;
        }
        if (r.ok) {
          const contentType = r.headers.get('content-type') || '';
          if (!contentType.includes('application/json')) {
            setError('notConfigured');
            return;
          }
          setAuthenticated(true);
          const saved = sessionStorage.getItem('cms_swagger_bearer');
          if (saved) setBearerToken(saved);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setLoginError('');
    try {
      const res = await fetch('/cms-swagger-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        if (bearerToken)
          sessionStorage.setItem('cms_swagger_bearer', bearerToken);
        setAuthenticated(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setLoginError(data.error || 'รหัสผ่านไม่ถูกต้อง');
      }
    } catch {
      setLoginError('เชื่อมต่อไม่ได้ กรุณาลองใหม่');
    }
    setSubmitting(false);
  };

  useEffect(() => {
    if (authenticated && !loading && swaggerLoaded && swaggerUIRef.current) {
      window.SwaggerUIBundle({
        domNode: swaggerUIRef.current,
        url: '/cms-swagger-spec',
        deepLinking: true,
        displayRequestDuration: true,
        filter: true,
        tryItOutEnabled: true,
        requestInterceptor: (req) => {
          const token =
            sessionStorage.getItem('cms_swagger_bearer') || bearerToken;
          if (token) {
            req.headers = req.headers || {};
            req.headers['Authorization'] = `Bearer ${token}`;
          }
          return req;
        },
      });
    }
  }, [authenticated, loading, bearerToken, swaggerLoaded]);

  if (loading) {
    return (
      <div style={styles.center}>
        <p style={{ color: '#888', fontFamily: 'sans-serif' }}>Loading...</p>
      </div>
    );
  }

  // Show critical errors only (swagger UI loading failures)
  if (error && error !== 'notConfigured') {
    return (
      <div style={styles.center}>
        <div style={styles.card}>
          <h2>Error</h2>
          <p style={styles.errorText}>{error}</p>
        </div>
      </div>
    );
  }

  if (error === 'notConfigured') {
    return (
      <div style={styles.center}>
        <div style={styles.card}>
          <h2>Not found</h2>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div style={styles.center}>
        <div style={styles.card}>
          <h1 style={styles.title}>
            <span style={{ fontSize: 32 }}>📄</span> CMS API Docs
          </h1>
          <p style={styles.subtitle}>กรอกรหัสผ่านเพื่อเข้าถึง Swagger UI</p>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="SWAGGER_PASSWORD"
              style={styles.input}
              autoFocus
              disabled={submitting}
            />
            <p
              style={{
                ...styles.subtitle,
                marginTop: 16,
                marginBottom: 4,
                textAlign: 'left',
              }}
            >
              Bearer Token (optional)
            </p>
            <textarea
              value={bearerToken}
              onChange={(e) => setBearerToken(e.target.value)}
              placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
              style={{
                ...styles.input,
                height: 72,
                resize: 'vertical',
                fontFamily: 'monospace',
                fontSize: 12,
              }}
              disabled={submitting}
            />
            {loginError && <p style={styles.errorText}>{loginError}</p>}
            <button type="submit" style={styles.button} disabled={submitting}>
              {submitting ? 'กำลังตรวจสอบ...' : 'เข้าสู่ระบบ'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const handleLogout = async () => {
    await fetch('/cms-swagger-logout', { method: 'POST' });
    sessionStorage.removeItem('cms_swagger_bearer');
    window.location.reload();
  };

  return (
    <div>
      <style>{`
        html, body { margin: 0; overflow: auto !important; height: auto !important; }
        .swagger-ui .topbar { display: none; }
      `}</style>
      <div style={styles.logoutBar}>
        <span style={styles.logoutLabel}>CMS API Docs</span>
        <button onClick={handleLogout} style={styles.logoutButton}>
          Logout
        </button>
      </div>
      <div ref={swaggerUIRef} id="swagger-ui"></div>
    </div>
  );
}

const styles = {
  center: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: '#f5f5f5',
    fontFamily: "'Segoe UI', sans-serif",
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    padding: '40px 48px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
    width: 360,
    textAlign: 'center',
  },
  title: {
    margin: '0 0 8px',
    fontSize: 24,
    fontWeight: 700,
    color: '#1a1a1a',
  },
  subtitle: {
    margin: '0 0 24px',
    color: '#666',
    fontSize: 14,
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    fontSize: 15,
    border: '1px solid #ddd',
    borderRadius: 8,
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: 8,
  },
  errorText: {
    color: '#e53e3e',
    fontSize: 13,
    margin: '0 0 8px',
  },
  button: {
    width: '100%',
    padding: '10px 0',
    fontSize: 15,
    fontWeight: 600,
    background: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    marginTop: 8,
  },
  logoutBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 20px',
    background: '#1b1b1b',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
  },
  logoutLabel: {
    color: '#fff',
    fontFamily: 'sans-serif',
    fontSize: 14,
    fontWeight: 600,
  },
  logoutButton: {
    padding: '6px 16px',
    fontSize: 13,
    fontWeight: 600,
    background: 'transparent',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.4)',
    borderRadius: 6,
    cursor: 'pointer',
  },
};

export default ApiDocsPage;
